import { Injectable, signal, computed, inject, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthLink, confirmAuthLink, readAuthLink } from './auth-link';

const E2E_AUTH_KEY = 'emerald:e2e-auth';

export interface UserProfile {
  id: string;
  email: string;
  role: 'admin' | 'operator' | 'owner';
  owner_id: string | null;
  /** Свързаното лице — името за поздрава и езикът за след вход (#288). */
  owner?: { display_name: string; preferred_language: 'bg' | 'en' } | null;
}

interface E2eAuthState {
  profile: UserProfile;
  accessToken?: string;
}

declare global {
  interface Window {
    Cypress?: unknown;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly supabase: SupabaseClient;

  private readonly _session = signal<Session | null>(null);
  private readonly _profile = signal<UserProfile | null>(null);
  private readonly _loading = signal(true);
  private readonly _authLink = signal<AuthLink>({ kind: null, failed: false });
  private readonly linkHash: string;
  private e2eAuthActive = false;
  /** Докато тече вход, 401 от `/me` е отказ на входа, не изтекла сесия (#399). */
  private signingIn = false;

  readonly session = this._session.asReadonly();
  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => !!this._session());
  readonly role = computed(() => this._profile()?.role ?? null);
  readonly hasOwnerLink = computed(() => !!this._profile()?.owner_id);
  /** Началната страница на влезлия потребител: порталът за собственик, таблото за служител. */
  readonly homePath = computed(() => (this.role() === 'owner' ? '/portal/dashboard' : '/dashboard'));
  /** Линкът от писмо за вход, с който е отворено приложението (покана или забравена парола). */
  readonly authLink = this._authLink.asReadonly();

  constructor() {
    // Hash-ът се чете преди createClient: supabase-js го изчиства, щом вземе сесията от него.
    this.linkHash = window.location.hash;
    this._authLink.set(readAuthLink(this.linkHash));
    this.supabase = createClient(environment.supabaseUrl, environment.supabaseAnonKey);
  }

  async init(): Promise<void> {
    if (this.loadE2eAuthState()) return;

    const { data } = await this.supabase.auth.getSession();
    this._session.set(data.session);
    this._authLink.set(confirmAuthLink(this._authLink(), this.linkHash, data.session?.access_token ?? null));
    if (data.session) await this.loadProfile();
    this._loading.set(false);

    this.supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') this._authLink.set({ kind: 'recovery', failed: false });
      this._session.set(session);
      if (session) {
        await this.loadProfile();
      } else {
        this._profile.set(null);
      }
    });
  }

  private loadE2eAuthState(): boolean {
    if (environment.production) return false;
    if (!window.Cypress) return false;

    const rawState = localStorage.getItem(E2E_AUTH_KEY);
    if (!rawState) return false;

    try {
      const state = JSON.parse(rawState) as E2eAuthState;
      const expiresAt = Math.floor(Date.now() / 1000) + 3600;

      // Cypress uses this hook to bypass external Supabase auth in e2e tests.
      this._session.set({
        access_token: state.accessToken ?? 'e2e-token',
        token_type: 'bearer',
        expires_in: 3600,
        expires_at: expiresAt,
        refresh_token: 'e2e-refresh-token',
        user: {
          id: state.profile.id,
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated',
          created_at: new Date().toISOString(),
        },
      } as Session);
      this._profile.set(state.profile);
      this._loading.set(false);
      this.e2eAuthActive = true;
      return true;
    } catch {
      localStorage.removeItem(E2E_AUTH_KEY);
      return false;
    }
  }

  async refreshProfile(): Promise<void> {
    await this.loadProfile();
  }

  private async loadProfile(): Promise<void> {
    try {
      const data = await firstValueFrom(
        this.http.get<{ user: UserProfile }>(`${environment.apiUrl}/me`),
      );
      this._profile.set(data.user);
    } catch {
      this._profile.set(null);
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    this.signingIn = true;
    try {
      const { error } = await this.supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await this.loadProfile();
      if (!this._profile()) throw new Error('Profile not found');
    } finally {
      this.signingIn = false;
    }
  }

  /**
   * API-то отхвърли токена на влезлия потребител (401) — например админ е
   * премахнал достъпа му (#399). Сесията се изчиства локално и веднага, така
   * че едновременните 401 да не я приключват втори път; GoTrue не се пита,
   * защото потребителят може вече да не съществува там.
   *
   * Връща `true` само на първия извикал — той води към входа. По време на
   * вход връща `false`: там 401 е отказ на входа и екранът му го показва.
   */
  endRejectedSession(): boolean {
    if (this.signingIn || !this._session()) return false;
    this._session.set(null);
    this._profile.set(null);
    if (this.e2eAuthActive) {
      localStorage.removeItem(E2E_AUTH_KEY);
    } else {
      void this.supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
    }
    return true;
  }

  /** Задава парола на влезлия потребител — след покана или забравена парола. */
  async updatePassword(password: string): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({ password });
    if (error) throw error;
    this._authLink.set({ kind: null, failed: false });
  }

  /**
   * Праща линк за нова парола. GoTrue отговаря еднакво за регистриран и
   * непознат имейл, така че грешка тук значи само неуспешна заявка.
   */
  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/set-password`,
    });
    if (error) throw error;
  }

  async signOut(): Promise<void> {
    if (this.e2eAuthActive) {
      localStorage.removeItem(E2E_AUTH_KEY);
      this._session.set(null);
      this._profile.set(null);
      return;
    }

    await this.supabase.auth.signOut();
  }

  async getAccessToken(): Promise<string | null> {
    // Чете се извън реактивния контекст: всяка HTTP заявка минава оттук
    // синхронно, а ефект, който тръгва да зарежда, не бива да зависи от
    // сесията — supabase-js я подменя при всяко връщане в таба (#397).
    const session = untracked(this._session);

    if (!session) return null;

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at ?? 0;

    if (expiresAt > now + 30) {
      return session.access_token;
    }

    // Token is expired or about to expire — trigger an explicit refresh
    try {
      const { data } = await this.supabase.auth.refreshSession();
      if (data.session) {
        this._session.set(data.session);
        return data.session.access_token;
      }
      this._session.set(null);
      this._profile.set(null);
      return null;
    } catch {
      this._session.set(null);
      this._profile.set(null);
      return null;
    }
  }
}
